import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

/** Write a Mermaid diagram with live preview and a few starter templates. */
export function KbDiagramDialog({ open, onOpenChange, templates, initial, onSave }) {
  const [code, setCode] = useState("");
  const [caption, setCaption] = useState("");
  const [error, setError] = useState("");
  const [rendering, setRendering] = useState(false);
  const previewRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setCode(initial?.code || templates["Call flow"]);
    setCaption(initial?.caption || "");
  }, [open]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open || !previewRef.current) return undefined;
    let alive = true;
    setRendering(true);
    const t = setTimeout(async () => {
      try {
        const { default: mermaid } = await import("mermaid");
        mermaid.initialize({ startOnLoad: false, theme: "neutral", securityLevel: "strict" });
        const { svg } = await mermaid.render(`kb-mmd-preview-${Date.now()}`, code || "flowchart TD\n A");
        if (alive && previewRef.current) { previewRef.current.innerHTML = svg; setError(""); }
      } catch (e) {
        if (alive) setError(String(e?.message || e).split("\n")[0].slice(0, 200));
      } finally {
        if (alive) setRendering(false);
      }
    }, 400);
    return () => { alive = false; clearTimeout(t); };
  }, [code, open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl" data-testid="kb-diagram-dialog">
        <DialogHeader>
          <DialogTitle>{initial ? "Edit diagram" : "Insert diagram"}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-wrap gap-1.5">
          {Object.keys(templates).map((name) => (
            <button key={name} type="button" data-testid={`kb-diagram-tpl-${name.replace(/\s+/g, "-").toLowerCase()}`}
              onClick={() => setCode(templates[name])}
              className="rounded-full border border-slate-200 dark:border-slate-800 px-2.5 py-1 text-xs font-medium text-slate-600 dark:text-slate-300 hover:border-primary/40 hover:bg-primary/5 hover:text-primary">
              {name}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          <div className="space-y-1.5">
            <Label className="text-xs">Diagram definition (Mermaid)</Label>
            <textarea data-testid="kb-diagram-code" value={code} rows={14}
              onChange={(e) => setCode(e.target.value)} spellCheck={false}
              className="w-full rounded-md border border-slate-200 dark:border-slate-800 px-2.5 py-2 font-mono text-xs focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary" />
            <Input data-testid="kb-diagram-caption" value={caption} placeholder="Caption (optional)"
              onChange={(e) => setCaption(e.target.value)} className="h-8 text-xs" />
            {error && <p className="text-[11px] text-rose-600" data-testid="kb-diagram-error">{error}</p>}
          </div>
          <div className="rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-3">
            <div className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
              Preview {rendering && <span className="font-normal normal-case text-slate-400 dark:text-slate-500">— rendering…</span>}
            </div>
            <div ref={previewRef} data-testid="kb-diagram-preview" className="overflow-auto text-center [&_svg]:mx-auto [&_svg]:max-w-full" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="kb-diagram-save" disabled={!code.trim()}
            onClick={() => { onSave({ code: code.trim(), caption: caption.trim() }); onOpenChange(false); }}>
            {initial ? "Update diagram" : "Insert diagram"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
